import { Injectable } from '@angular/core';
declare const THREE: any;
import { StageService } from './stage.service';
import { SceneUtilsService } from './scene-utils.service';
import { Select3dService } from './select-3d.service';
import { ConstantsService } from './constants.service';
import { StraightStairService } from './straight-stairs.service';
import { StairLandingLeftService } from './stair-landing-left.service';
import { StairLandingRightService } from './stair-landing-right.service';
import { StairLandingStraightService } from './stair-landing-straight.service';
import { StairLandingBothSidesService } from './stair-landing-both-sides.service';
import { BuildingCheckerService } from './building-checker.service';
import { ToasterService } from 'angular2-toaster';
import { InteractiveSpriteComponentBase } from '../interactive-sprite-component-base';
import {EventLogService, Actions} from './eventLog';
import {AutoScaleSprite} from '../custom/AutoScaleSprite';
import { DataForDisplayingMenuItemsService } from './data-for-displaying-menu-items.service';

@Injectable()
export class GenerateStairsService extends InteractiveSpriteComponentBase {

  private sprites = [];
  private loader = new THREE.TextureLoader();
  private raycaster: any = new THREE.Raycaster();
  private currentStairType: any;
  private selectTierSubscription: any;

  private stairTypeErrorMap = {
    straightStair: 'Straight stair can only be added if deck height is greater than 1\'8"',
    stairLandingLeft: 'Stair landing left can only be added if deck height is greater than 4\'0"',
    stairLandingRight: 'Stair landing right can only be added if deck height is greater than 4\'0"',
    stairLandingStraight: 'Stair landing straight can only be added if deck height is greater than 1\'8"',
    stairLandingBothSides: 'Stair landing both sides can only be added if deck height is greater than 4\'0"'
  }

  constructor(
    private stage: StageService,
    private sceneUtils: SceneUtilsService,
    private constants: ConstantsService,
    private select3dService: Select3dService,
    private straightStairService: StraightStairService,
    private stairLandingLeftService: StairLandingLeftService,
    private stairLandingRightService: StairLandingRightService,
    private stairLandingStraightService: StairLandingStraightService,
    private stairLandingBothSidesService: StairLandingBothSidesService,
    private buildingCheckerService: BuildingCheckerService,
    private toasterService: ToasterService,
    private eventLogSrvc: EventLogService,
    private dataForDisplayingMenuItemsService: DataForDisplayingMenuItemsService
  ) {
    super();
    this.disable();
  }

  drawSprites(stairType) {
    this.currentStairType = stairType;

    const floor = this.select3dService.getSelectedTier();
    const points = floor.userData.points;

    const height = floor.getWorldPosition(new THREE.Vector3()).y;

    if (this.buildingCheckerService.checkHeightForStairs(stairType, height)) {
      for (let i = 0; i < points.length; i++) {
        if (
          floor.userData.edges[i].length >=
          this.constants.minimalSpaceForStairsOnSIde * 2 + this.constants.inermediateStepSize.width &&
          !points[i].superstructureType
        ) {

          const spriteMap = this.loader.load('../../assets/images/sprites/straight_step.png');

          const spriteMaterial = new THREE.SpriteMaterial({ map: spriteMap, color: 0xffffff, depthTest: false, depthWrite: false });

          const sprite = new AutoScaleSprite(50, 50, spriteMaterial);
          sprite.userData.edge = floor.userData.edges[i];
          sprite.userData.point = points[i];
          sprite.userData.floorId = floor.id;
          sprite.userData.height = height;
          sprite.userData.prevPoint = i > 0 ? points[i - 1] : points[points.length - 1];
          sprite.userData.nextPoint = (i < points.length - 2) ? points[i + 2] : ((i === points.length - 2) ? points[0] : points[1]);

          this.sprites.push(sprite);
          sprite.position.set(
            (points[i].x + points[(i + 1) % points.length].x) / 2,
            height,
            (points[i].y + points[(i + 1) % points.length].y) / 2
          );
          this.stage.add(sprite);
        }
      }
    }
    else {
      this.showToast('warning', 'Warning', this.stairTypeErrorMap[stairType]);
      this.disable();
    }
  }

  removeSprites() {
    this.stage.removeMany(this.sprites);
    this.sprites.splice(0, this.sprites.length);
  }

  mouseDown(e) {
    e.stopPropagation();
    const p = this.sceneUtils.getRay(e.offsetX, e.offsetY);
    this.raycaster.ray.origin.copy(p.ray.origin);
    this.raycaster.ray.direction.copy(p.ray.direction);

    const intersections = this.raycaster.intersectObjects(this.sprites);
    if (intersections.length) {
      const intersection = intersections[0].object;
      const config = {
        firstPoint: intersection.userData.edge.firstPoint,
        secondPoint: intersection.userData.edge.secondPoint,
        height: intersection.userData.height,
        distance: intersection.userData.edge.length / 2,
        floorId: intersection.userData.floorId,
        width: this.constants.inermediateStepSize.width, 
        prevPoint: intersection.userData.prevPoint,
        nextPoint: intersection.userData.nextPoint
      };

      let result = Promise.resolve(null);

      switch (this.currentStairType) {
        case 'straightStair':
          result = this.straightStairService.build(config);
          break;
        case 'stairLandingLeft':
          result = this.stairLandingLeftService.build(config);
          break;
        case 'stairLandingRight':
          result = this.stairLandingRightService.build(config);
          break;
        case 'stairLandingStraight':
          result = this.stairLandingStraightService.build(config);
          break;
        case 'stairLandingBothSides':
          result = this.stairLandingBothSidesService.build(config);
          break;
      }

      result.then((stairs) => {
        if (stairs) {
          stairs.traverse(ch => ch.receiveShadow = ch.name === 'Steps');
          this.eventLogSrvc.dispatch(new Actions.AddStairsAction(stairs.userData.config.superstructureId));
          this.dataForDisplayingMenuItemsService.needRailingRebuildIfVisible.next();
        }
      });

      this.removeSprites();
    }
  }

  showToast(type: string, title: string, body: string) {
    this.toasterService.popAsync(type, title, body);
  }

  disable() {
    this.removeSprites();
    this.selectTierSubscription && this.selectTierSubscription.unsubscribe();
    super.disable();
  }

  enable() {
    super.enable();
    if (!this.selectTierSubscription || this.selectTierSubscription.isStopped) {
      this.selectTierSubscription = this.select3dService.selectedTierSubject.subscribe(value => {
        if (this.isEnabled()) {
          this.removeSprites();
          this.drawSprites(this.currentStairType);
        }
      });
    }
  }

}



// WEBPACK FOOTER //
// ./src/app/services/generate-stairs.service.ts